import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Container, Grid, Stack, Typography } from '@mui/material';
import { Box } from '@mui/system';
// icons
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CampaignIcon from '@mui/icons-material/Campaign';
import DateRangeIcon from '@mui/icons-material/DateRange';
import InterestsIcon from '@mui/icons-material/Interests';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLocation } from 'react-router-dom';
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar';
import SourceIcon from '@mui/icons-material/Source';
import StayCurrentPortraitIcon from '@mui/icons-material/StayCurrentPortrait';
import { useForm } from 'react-hook-form';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import UpdateIcon from '@mui/icons-material/Update';
// react icons
import { useDispatch } from 'react-redux';
import { BsTelephone } from 'react-icons/bs';
import { FaRegUser } from 'react-icons/fa';
import { GiModernCity, GiSwapBag } from 'react-icons/gi';
import { HiOutlineMail } from 'react-icons/hi';
// components
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import { fDate } from '../../utils/formatTime';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import useCallCenterSelector from '../../store/Slice/CallCenterList/CallCenterListSelector';
import ViewCardComponent from '../../components/ViewCards';
import CustomTableComponent from '../../components/CustomTable';
import {
  getAllEmpMasterList,
  getCityData,
  getLoanTypeData,
  getStateData,
  setCityData
} from '../../store/Slice/Global/GlobalSlice';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import { getCallCenterView } from '../../store/Slice/CallCenterList/CallCenterListSlice';
import CustomInput from '../../components/CustomInput';
import CustomSelect from '../../components/CustomSelect';
import CustomDialog from '../../components/CustomDialogue';
import { addProspectData } from '../../store/Slice/Prospect/ProspectSlice';

// export const validationSchema = yup.object().shape({
//   mobile: yup
//     .string()
//     .required('Mobile is required')
//     .matches(/^[6-9][0-9]{9}$/, 'Enter a valid 10-digit mobile number'),

//   customerName: yup
//     .string()
//     .required('Name is required')
//     .max(50, 'Name must be less than 50 characters'),

//   customerEmail: yup
//     .string()
//     .required('Email is required')
//     .matches(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/, 'Enter a valid email'),

//   provinceId: yup
//     .string()
//     .nullable()
//     .required('State is required')
//     .notOneOf(['', ' ', null], 'Please select a valid State'),

//   districtId: yup
//     .string()
//     .nullable()
//     .required('City is required')
//     .notOneOf(['', ' ', null], 'Please select a valid City'),

//   loanType: yup
//     .string()
//     .nullable()
//     .required('Loan Type is required')
//     .notOneOf(['', ' ', null], 'Please select a valid Loan Type'),

//   loanAmountRequested: yup
//     .number()
//     .typeError('Loan Amount must be a number')
//     .required('Loan Amount is required')
//     .positive('Loan Amount must be greater than 0')
//     .max(1000000000, 'Please enter amount less than 10,00,00,000'),

//   applicantEmploymentTypeId: yup
//     .string()
//     .nullable()
//     .required('Employment Type is required')
//     .notOneOf(['', ' ', null], 'Please select a valid Employment Type')
// });

function ViewCallCenter() {
  const [isEdit, setIsEdit] = useState(false);

  const location = useLocation();
  const dispatch = useDispatch();

  const { rowId } = location?.state || {}; // safely access rowId
  const { LoginData } = useLoginSelector();
  const { stateDrop, CityDrop, LoanTypeDrop, AllEmpMasterDrop } = useGlobalSelector();
  const { CallViewData: viewCallCenterData = {}, CallViewData } = useCallCenterSelector();

  const {
    handleSubmit: handleReduxSubmit,
    control,
    setValue,
    reset,
    formState: { errors }
  } = useForm({
    mode: 'onChange', // Add this to validate on blur
    defaultValues: {
      customerName: '',
      mobile: '',
      customerEmail: '',
      loanAmountRequested: '',
      loanTypeId: '',
      misMonth: ''
    }
  });

  // call center view
  const getCallCenterViewApi = useCallback(() => {
    const finalObject = {
      leadId: rowId,
      userId: LoginData?.userId,
      companyId: LoginData?.companyId
    };
    dispatch(getCallCenterView(finalObject));
  }, [LoginData, rowId]);

  // loan master
  const getLoanMasterApi = useCallback(() => {
    const obj = {
      companyId: LoginData?.companyId
    };
    dispatch(getLoanTypeData(obj));
  }, [LoginData]);

  // all employee  master list
  const getEmployeeMasterApi = useCallback(() => {
    const obj = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId
    };
    dispatch(getAllEmpMasterList(obj));
  }, [LoginData]);

  //  state list
  const getStateList = () => {
    const data = {
      countryId: 101
    };
    dispatch(getStateData(data));
  };

  // show edit card
  const handleEditCard = (event, fields) => {
    const editData = CallViewData?.editProspectDetails || {};
    reset();
    getLoanMasterApi(); // loan type list
    getStateList(); //  state list
    getEmployeeMasterApi(); // all emp list
    handleStateChange(editData?.stateId); // city list
    setIsEdit(true);

    setValue('customerName', editData?.name || '');
    setValue('customerEmail', editData?.email || '');
    setValue('mobile', editData?.phone || '');
    setValue('loanAmountRequested', editData?.loanAmount || '');
    setValue('provinceId', editData?.stateId);
    setValue('districtId', editData?.cityId);
    setValue('loanType', editData?.loanId);
    setValue('applicantEmploymentTypeId', editData?.employmentTypeId);
  };

  // close edit card
  const handleCloseCard = (event) => {
    setIsEdit(false);
  };

  //  state change
  const handleStateChange = (optionVal) => {
    if (!optionVal) {
      dispatch(setCityData([])); // city data
      setValue('districtId', null, { shouldValidate: true });
      return '';
    }
    const data = {
      stateId: optionVal
    };
    dispatch(getCityData(data)); // city data
  };

  // edit submit api
  const onSubmit = useCallback(
    async (data) => {
      const finalData = {
        ...data,
        leadId: rowId,
        companyId: LoginData?.companyId,
        userId: LoginData?.userId
      };

      try {
        const res = await dispatch(addProspectData(finalData));
        if (res?.status === 'success') {
          getCallCenterViewApi();
          handleCloseCard();
        }
      } catch (error) {
        console.error('Error submitting form:', error);
      }
    },
    [LoginData, rowId] // 🔹 depends on LoginData
  );

  useEffect(() => {
    getCallCenterViewApi();
  }, [getCallCenterViewApi]);

  const applicationDetailsFields = useMemo(
    () => [
      {
        label: 'Prospect ID',
        value: viewCallCenterData?.applicationDetails?.prospectId,
        icon: <PermContactCalendarIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'Campaign Source',
        value: viewCallCenterData?.applicationDetails?.campaignSource,
        icon: <SourceIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'Campaign Name',
        value: viewCallCenterData?.applicationDetails?.campaignName,
        icon: <CampaignIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'Prospect Date',
        value: viewCallCenterData?.applicationDetails?.prospectDate
          ? fDate(viewCallCenterData.applicationDetails.prospectDate)
          : null,
        icon: <DateRangeIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'Prospect Count',
        value: viewCallCenterData?.applicationDetails?.prospectCount,
        icon: <SupervisorAccountIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'Assigned On',
        value: viewCallCenterData?.applicationDetails?.assignedOn
          ? fDate(viewCallCenterData.applicationDetails.assignedOn)
          : null,
        icon: <AssignmentIndIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'Current Stage',
        value: viewCallCenterData?.applicationDetails?.currentStage,
        icon: <StayCurrentPortraitIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'Last Update Date',
        value: viewCallCenterData?.applicationDetails?.lastUpdateDate
          ? fDate(viewCallCenterData.applicationDetails.lastUpdateDate)
          : null,
        icon: <UpdateIcon className="viewLabelIcon" size={24} />
      }
    ],
    [viewCallCenterData]
  );

  const loanRequirementFields = useMemo(
    () => [
      {
        label: 'Name',
        value: viewCallCenterData?.loanRequirement?.name,
        icon: <FaRegUser className="viewLabelIcon" size={24} />
      },
      {
        label: 'Phone',
        value: viewCallCenterData?.loanRequirement?.phone,
        icon: <BsTelephone className="viewLabelIcon" size={24} />
      },
      {
        label: 'Loan Type',
        value: viewCallCenterData?.loanRequirement?.loanType,
        icon: <InterestsIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'Employment Type',
        value: viewCallCenterData?.editProspectDetails?.employmentType,
        icon: <InterestsIcon className="viewLabelIcon" size={24} />
      },
      {
        label: 'Loan Amount',
        value: viewCallCenterData?.loanRequirement?.loanAmount
          ? Number(viewCallCenterData.loanRequirement.loanAmount)
          : null,
        icon: <GiSwapBag className="viewLabelIcon" size={24} />
      },
      {
        label: 'City',
        value: viewCallCenterData?.loanRequirement?.city,
        icon: <GiModernCity className="viewLabelIcon" size={24} />
      },
      {
        label: 'Email',
        value: viewCallCenterData?.loanRequirement?.email,
        icon: <HiOutlineMail className="viewLabelIcon" size={24} />
      }
    ],
    [viewCallCenterData]
  );

  const callLogColumns = [
    { label: 'Call Date', key: 'callDateTime', formatter: fDate },
    { label: 'Call Agent', key: 'callAgentName' },
    { label: 'Status', key: 'callStatus' },
    { label: 'Comments', key: 'callComments' }
  ];

  return (
    <Page title="Partner Management System || Call Center">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="View Call Center" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          <Box container textAlign="right">
            <Box className="statusBox">
              <Typography variant="body2">Current Status:</Typography>
              <Typography variant="subtitle2" className="disbursedText">
                {viewCallCenterData?.applicationDetails?.currentStatus
                  ? viewCallCenterData.applicationDetails.currentStatus
                  : 'N/A'}
              </Typography>
            </Box>
          </Box>
          <Grid container spacing={3}>
            <ViewCardComponent title="Application Details" fields={applicationDetailsFields} />
            <ViewCardComponent
              title="Loan Requirement"
              fields={loanRequirementFields}
              handleEdit={handleEditCard}
            />
          </Grid>

          <Typography variant="body1" className="headingLineUp headingLineUpOut">
            Call Log
          </Typography>
          <Box className="viewLeadCard cardTopMinusMargin viewCardPadding">
            <CustomTableComponent
              columns={callLogColumns || []}
              data={viewCallCenterData?.callLog || []}
            />
          </Box>
        </Box>

        <Box>
          <CustomDialog
            showCloseIcon
            open={isEdit}
            onClose={(event, reason) => {
              if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                handleCloseCard();
              }
            }}
            title="Edit Loan Requirement"
            content={
              <Box mb={3}>
                <form id="EditLoanRequirementForm" onSubmit={handleReduxSubmit(onSubmit)}>
                  <Grid container spacing={3} mb={3}>
                    <Grid item xs={6}>
                      <CustomInput
                        name="customerName"
                        label="Name"
                        control={control}
                        errors={errors}
                        isRequired
                        fieldCode="NAME"
                        fieldType="ALPHABET"
                      />{' '}
                    </Grid>
                    <Grid item xs={6}>
                      <CustomInput
                        name="mobile"
                        label="Mobile"
                        control={control}
                        errors={errors}
                        isRequired
                        fieldCode="MOBILE"
                        fieldType="NUMBER_FIELD"
                        type="Number"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <CustomInput
                        name="customerEmail"
                        label="Email"
                        control={control}
                        errors={errors}
                        isRequired
                        fieldCode="EMAIL"
                        fieldType="TEXT_FIELD"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <CustomSelect
                        name="provinceId"
                        label="State"
                        control={control}
                        errors={errors}
                        options={stateDrop || []}
                        isRequired
                        onChange={(event, val, option, reason, details) => {
                          handleStateChange(val);
                        }}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <CustomSelect
                        name="districtId"
                        label="City"
                        control={control}
                        errors={errors}
                        options={CityDrop || []}
                        isRequired
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <CustomSelect
                        name="loanType"
                        label="Loan Type"
                        control={control}
                        errors={errors}
                        options={LoanTypeDrop || []}
                        isRequired
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <CustomSelect
                        label="Employment Type"
                        name="applicantEmploymentTypeId"
                        control={control}
                        errors={errors}
                        options={AllEmpMasterDrop || []}
                        isRequired
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <CustomInput
                        name="loanAmountRequested"
                        label="Loan Amount"
                        type="Number"
                        control={control}
                        errors={errors}
                        isRequired
                        // fieldCode="AMOUNT"
                        fieldType="AMOUNT_FIELD"
                      />
                    </Grid>
                  </Grid>{' '}
                </form>{' '}
              </Box>
            }
            actions={
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: '#f5f5f5', // light grey
                    color: '#000', // black text
                    '&:hover': {
                      backgroundColor: '#e0e0e0' // darker grey on hover
                    }
                  }}
                  onClick={handleCloseCard}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  form="EditLoanRequirementForm"
                  variant="contained"
                  className="mainBtn"
                  color="primary"
                >
                  Save{' '}
                </Button>
              </Box>
            }
          />
        </Box>
      </Container>
    </Page>
  );
}
export default ViewCallCenter;
